import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Auth, createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, filter, finalize, firstValueFrom, from, map, of, switchMap, take, tap, timeout } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppConfig } from '../../../../config/appConfig';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/user.model';
import { SubscriptionService } from '../../../services/subscription.service';
import { AccountContextService } from '../../../services/account-context.service';
import { PublicBrandingService } from '../../../services/public-branding.service';
import { AppSettingsService } from '../../../services/app-settings.service';
import { AffiliateReferralTrackingService } from '../../../services/affiliate-referral-tracking.service';
import { AuthOnboardingService } from '../../../services/auth-onboarding.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent implements OnInit {
  title = AppConfig.title;
  registerForm: FormGroup;
  redirectTo: string | null = null;
  accountAlias: string | null = null;
  private registrationInProgress = false;
  readonly isSubmitting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly brandingProfile = toSignal(this.publicBrandingService.brandingProfile$, { initialValue: null });
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private auth: Auth,
    private readonly route: ActivatedRoute,
    private router: Router,
    private readonly authService: AuthService,
    private readonly subscriptionService: SubscriptionService,
    private readonly accountContextService: AccountContextService,
    private readonly publicBrandingService: PublicBrandingService,
    private readonly affiliateReferralTrackingService: AffiliateReferralTrackingService,
    private readonly authOnboardingService: AuthOnboardingService,
    readonly appSettingsService: AppSettingsService
  ) {
    this.registerForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: [''],
      acceptedLegal: [false, Validators.requiredTrue]
    }, { validators: this.matchPasswords });
  }

  matchPasswords(group: FormGroup) {
    return group.get('password')?.value === group.get('confirmPassword')?.value
      ? null : { mismatch: true };
  }

  ngOnInit(): void {
    this.redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');
    this.accountAlias = this.route.snapshot.queryParamMap.get('ref') ?? this.affiliateReferralTrackingService.getStoredReferralCode();
    this.authService.user$
      .pipe(
        filter((user): user is User => !!user),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.registrationInProgress) {
          this.navigateAfterRegister();
        }
      });
  }

  onRegister() {
    if (!this.appSettingsService.registrationEnabled()) {
      alert('Registration is currently disabled. Please try again later.');
      return;
    }

    if (this.registerForm.invalid) return;

    this.registrationInProgress = true;
    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    const { firstName, lastName, email, password } = this.registerForm.value;
    const fullName = `${firstName} ${lastName}`.trim();

    from(createUserWithEmailAndPassword(this.auth, email, password)).pipe(
      map(userCredential => userCredential.user),
      map(user => {
        sessionStorage.setItem('pendingRegistrationProfile', JSON.stringify({ firstName, lastName, acceptedLegal: true }));
        return user;
      }),
      switchMap(user => from(updateProfile(user, { displayName: fullName })).pipe(map(() => user))),
      // The verification screen already tells the user to return to the app. Avoid
      // attaching a continue URL here: Firebase rejects the entire email request
      // when a deployment hostname has not yet been added to Authorized domains.
      switchMap(user => from(sendEmailVerification(user))),
      tap(() => void this.router.navigate(['/verify-email'], { queryParams: this.registerLinkQueryParams })),
      catchError(err => {
        this.registrationInProgress = false;
        console.error('Registration error:', err);
        this.errorMessage.set(this.friendlyAuthError(err));
        return EMPTY;
      }),
      finalize(() => this.isSubmitting.set(false))
    ).subscribe();
  }

  onGoogleSignIn(): void {
    if (!this.appSettingsService.registrationEnabled() || !this.registerForm.controls['acceptedLegal'].value) return;
    this.registrationInProgress = true;
    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    from(this.authOnboardingService.signInWithGoogle()).pipe(
      switchMap(user => from(this.authOnboardingService.ensureUserProvisioned(user, { acceptedLegal: true }))),
      switchMap(({ accountId }) => from(this.waitForRegisteredAccountContext(accountId))),
      tap(() => this.navigateAfterRegister()),
      catchError(err => {
        this.registrationInProgress = false;
        this.errorMessage.set(this.friendlyAuthError(err));
        return EMPTY;
      }),
      finalize(() => this.isSubmitting.set(false))
    ).subscribe();
  }


  private async waitForRegisteredAccountContext(accountId: string): Promise<void> {
    await firstValueFrom(
      this.accountContextService.activeMembership$.pipe(
        filter(membership => membership?.accountId === accountId),
        take(1),
        timeout({ first: 20_000 }),
        catchError(err => {
          console.warn('Registered account membership did not hydrate before dashboard navigation:', err);
          return of(null);
        }),
        map(() => undefined)
      )
    );
  }

  private navigateAfterRegister(): void {
    if (this.redirectTo) {
      this.router.navigateByUrl(this.redirectTo);
      return;
    }

    const selectedPlan = this.subscriptionService.getSelectedPlan();
    if (selectedPlan) {
      this.router.navigate(['/checkout']);
      return;
    }

    this.router.navigate(['/dashboard']);
  }

  private friendlyAuthError(error: { code?: string; message?: string }): string {
    if (error.code === 'auth/popup-closed-by-user') return 'Google sign-in was cancelled.';
    if (error.code === 'auth/account-exists-with-different-credential') {
      return 'An account already exists for this email. Sign in with your password first.';
    }
    if (error.code === 'auth/email-already-in-use') return 'An account already exists for this email address.';
    if (error.code === 'auth/unauthorized-continue-uri') {
      return 'The verification email could not be sent because this site is not authorized in Firebase. Please contact support.';
    }
    return error.message ?? 'We could not create your account. Please try again.';
  }

  get logoUrl(): string {
    const logoUrl = this.brandingProfile()?.companyInformation?.logoUrl?.trim() ?? '';
    return logoUrl || 'assets/yachtapp-logo-dark.png';
  }

  get logoAlt(): string {
    const companyName = this.brandingProfile()?.companyInformation?.name?.trim() ?? '';
    return companyName ? `${companyName} Logo` : `${this.title} Logo`;
  }

  get registerLinkQueryParams(): Record<string, string> | null {
    const params: Record<string, string> = {};
    if (this.redirectTo) {
      params['redirectTo'] = this.redirectTo;
    }
    if (this.accountAlias) {
      params['ref'] = this.accountAlias;
    }
    return Object.keys(params).length ? params : null;
  }
}
